/* global google */

// import Turbolinks from 'turbolinks';
import delay from 'delay';
import bind from 'bind-decorator';

// TODO: switch to original library when issue is fixed https://github.com/googlemaps/js-markerwithlabel/issues/562
// const { MarkerWithLabel } = await import('@googlemaps/markerwithlabel');
// https://github.com/morr/markerwithlabel
import { MarkerWithLabel } from '@morr/markerwithlabel';
// https://github.com/morr/snazzy-info-window fork with addEventListener fix
import SnazzyInfoWindow from '@morr/snazzy-info-window';

import { createBaloonApp } from '../balloon';
import { renderMarkerNode } from '../marker';

import { isMobile } from '@/helpers/mobile_detect';

const Z_INDEX_INITIAL = 1;
const Z_INDEX_FRONT = 10;

export default class Mark {
  marker = null;
  markData = null;
  gmap = null;

  isAutoOpen = false;

  infoWindow = null;

  constructor({
    markData,
    leadProps,
    gmap,
    isAutoOpen,
    isVueDataFlow,
    isNoBalloon,
    emitBalloonShow,
    emitBalloonHide
  }) {
    this.markData = markData;
    this.leadProps = leadProps;
    this.gmap = gmap;
    this.isAutoOpen = isAutoOpen;
    this.isVueDataFlow = isVueDataFlow;
    this.isNoBalloon = isNoBalloon;
    this.emitBalloonShow = emitBalloonShow;
    this.emitBalloonHide = emitBalloonHide;

    this.isActive = false;

    const markerData = {
      position: this.position,
      map: gmap,
      zIndex: Z_INDEX_INITIAL,
      icon: '/assets/static/b-map/1x1.png'
    };

    this.marker = this.buildMarker(markerData);

    this.marker.addListener('mouseover', this.toTop);
    this.marker.addListener('mouseout', this.toBottom);
    this.marker.addListener('click', this.toggleBalloon);
    this.marker.addListener('mousedown', this.mouseDown);
    this.marker.addListener('mouseup', this.mouseUp);

    if (this.isAutoOpen) {
      delay(250).then(this.showBalloon);
    }
  }

  get storageId() {
    return this.markData.id;
  }

  get position() {
    return {
      lat: parseFloat(this.markData.geo.latitude),
      lng: parseFloat(this.markData.geo.longitude)
    };
  }

  get $marker() {
    return $(this.marker.label.labelDiv).find('.b-map-marker-v4');
  }

  get baloonAppProps() {
    return {
      ...this.markData,
      leadProps: this.leadProps
    };
  }

  show() {
    this.marker.setVisible(true);
  }

  hide() {
    if (this.infoWindow?.isOpen()) {
      this.infoWindow.close();
    }

    this.marker.setVisible(false);
  }

  @bind
  toggleBalloon() {
    if (this.isActive) {
      this.hideBalloon();
    } else {
      this.showBalloon();
    }
  }

  @bind
  showBalloon() {
    if (this.isActive) { return; }

    if (this.isVueDataFlow) {
      this.emitBalloonShow(this);
    } else {
      this.executeShowBalloon();
    }
  }

  @bind
  hideBalloon() {
    if (!this.isActive) { return; }

    if (this.isVueDataFlow) {
      this.emitBalloonHide(this);
    } else {
      this.executeHideBalloon();
    }
  }

  async executeShowBalloon() {
    if (this.isActive) { return; }

    if (this.gmap.activeMarker) {
      this.gmap.activeMarker.executeHideBalloon();
    }

    this.isActive = true;
    this.toTop();
    this.$marker.addClass('is-active');

    this.gmap.activeMarker = this;

    if (this.isNoBalloon) { return; }

    if (isMobile()) {
      if (!this.$balloon) {
        this.$balloon = $('<div class="map-balloon-v4" />');
        (await createBaloonApp(this.baloonAppProps)).mount(this.$balloon[0]);

        this.$balloon.appendTo(this.gmap.getDiv());
        this.$balloon.find('.close').on('click', e => {
          e.preventDefault();
          this.hideBalloon();
        });
      } else {
        this.$balloon.showBalloon();
      }
    } else {
      this.infoWindow ||= await this.buildInfoWindow();
      this.infoWindow.open();
    }
  }

  @bind
  executeHideBalloon() {
    if (!this.isActive) { return; }

    this.gmap.activeMarker = null;

    this.isActive = false;
    this.toBottom();
    this.$marker.removeClass('is-active');

    if (this.isNoBalloon) { return; }

    if (isMobile()) {
      this.$balloon.hide();
    } else {
      if (this.infoWindow?.isOpen()) {
        this.infoWindow.close();
      }
    }
  }

  @bind
  toTop() {
    this.$marker.addClass('is-hover');
    this.marker.setOptions({ zIndex: Z_INDEX_FRONT });
  }

  @bind
  toBottom() {
    if (!this.isActive) {
      this.$marker.removeClass('is-hover');
      this.marker.setOptions({ zIndex: Z_INDEX_INITIAL });
    }
  }

  @bind
  mouseDown() {
    this.$marker.addClass('is-pressed');
  }

  @bind
  mouseUp() {
    this.$marker.removeClass('is-pressed');
  }

  @bind
  afterOpen() {
    $(this.infoWindow._html.content)
      .find('.close')
      .on('click', this.hideBalloon);
  }

  @bind
  beforeClose() {
    $(this.infoWindow._html.content)
      .find('.close')
      .off('click', this.hideBalloon);
  }

  async buildInfoWindow() {
    const balloonNode = document.createElement('div');
    (await createBaloonApp(this.baloonAppProps)).mount(balloonNode);

    return new SnazzyInfoWindow({
      marker: this.marker,
      content: balloonNode,
      placement: this.isAutoOpen ? 'right' : 'top',
      offset: this.isAutoOpen ?
        { top: '-30px', right: '-53px' } :
        { top: '-53px' },
      openOnMarkerClick: false,
      closeWhenOthersOpen: true,
      showCloseButton: false,
      callbacks: {
        afterOpen: this.afterOpen,
        beforeClose: this.beforeClose,
        afterClose: this.hideBalloon
      }
    });
  }

  buildMarker(markerData) {
    const { markerNode, width, height } = renderMarkerNode(this.markData, { isGoogle: true });

    return new MarkerWithLabel({
      ...markerData,
      labelContent: markerNode,
      labelAnchor: new google.maps.Point(-width / 2, -height + 5),
      labelClass: 'div',
      labelInBackground: true
    }, markerData);
  }

  // buildMarkerWoLabel(markerData) {
  //   return new google.maps.Marker(markerData);
  // }
}
